/**
 * Markdown representations of the public marketing pages, served to agents that
 * send `Accept: text/markdown` (see `lib/accept-markdown.ts`) and reused for the
 * 404 recovery body.
 *
 * These are hand-maintained rather than generated from the React tree: the pages
 * are client components full of animation and 3-D wrappers, so converting the
 * rendered DOM would produce noise. Keeping the prose here means the markdown an
 * agent reads stays a clean summary. It mirrors `public/llms-full.txt` — update
 * both when the marketing copy changes materially.
 */

export const SITE_URL = 'https://gamerplug.app'

/** Canonical entry points, listed on the 404 body so agents can recover. */
export const SITE_MAP_LINKS: ReadonlyArray<{ path: string; label: string; description: string }> = [
  { path: '/en', label: 'Home', description: 'What GamerPlug is, features, and how it works' },
  { path: '/en/games', label: 'Games', description: 'Every supported title and its player pool' },
  { path: '/en/tournaments', label: 'Tournaments', description: 'Community tournaments you can join' },
  { path: '/en/leaderboard', label: 'Leaderboard', description: 'Top players and competitors' },
  { path: '/en/blog', label: 'Blog', description: 'Articles, guides, and product updates' },
  { path: '/en/download', label: 'Download', description: 'Get the iOS and Android apps' },
  { path: '/en/team', label: 'Team', description: 'The people building GamerPlug' },
  { path: '/en/contact', label: 'Contact', description: 'Get in touch with the team' },
]

/** Machine-readable files an agent should read before crawling. */
export const MACHINE_READABLE_LINKS: ReadonlyArray<{ path: string; description: string }> = [
  { path: '/llms.txt', description: 'Site overview and page index for LLMs' },
  { path: '/llms-full.txt', description: 'Expanded plain-text description of GamerPlug' },
  { path: '/openapi.json', description: 'OpenAPI 3.1 description of the public HTTP API' },
  { path: '/sitemap.xml', description: 'Every indexable URL' },
  { path: '/robots.txt', description: 'Crawl policy' },
]

/**
 * Markdown body for a 404. Kept short and link-dense: its job is to tell an
 * agent where to look next, not to explain the product.
 */
export function notFoundMarkdown(pathname: string): string {
  const siteMap = SITE_MAP_LINKS.map(
    ({ path, label, description }) => `- [${label}](${SITE_URL}${path}) — ${description}`
  ).join('\n')

  const machineReadable = MACHINE_READABLE_LINKS.map(
    ({ path, description }) => `- [${path}](${SITE_URL}${path}) — ${description}`
  ).join('\n')

  return `# 404 — Page not found

\`${pathname}\` does not exist on gamerplug.app. This response is a real HTTP 404;
treat the path as invalid rather than retrying it.

## Where to look next

${siteMap}

## Machine-readable files

${machineReadable}

## Notes for agents

- Marketing pages are locale-prefixed: \`/en\`, \`/es\`, \`/ja\`. A path without a
  locale prefix redirects (307) to the \`/en\` equivalent.
- Every page listed above also serves markdown when requested with
  \`Accept: text/markdown\`.
- API endpoints live under \`/api/\` and always answer with JSON, including errors.
  Unknown \`/api/\` paths return a 404 JSON body with an \`error.code\` of
  \`route_not_found\`.
`
}

/** Markdown for the homepage, served on `Accept: text/markdown` for `/`, `/en`, … */
export function homeMarkdown(locale: string): string {
  const localeNote =
    locale === 'en'
      ? ''
      : `\n> This is the English markdown representation. The HTML page at ${SITE_URL}/${locale} is localized.\n`

  return `# GamerPlug — No more randoms.

> GamerPlug is an AI-powered matchmaking app that connects gamers with teammates
> who share their playstyle, skill level, and games — across PC, PlayStation,
> Xbox, and mobile.
${localeNote}
Get your ideal player plugged in. Instead of queuing with strangers, GamerPlug
matches you with players who actually complement your gameplay, then helps you
build duos, squads, and tournament teams.

## Key features

- **Clip-first profiles** — every profile leads with gameplay footage, and a profile without clips never enters anyone's deck.
- **Synced stats** — Apex Legends, Fortnite, and League of Legends ranks are pulled from the game's official API, not self-reported.
- **Squad posts** — post the mode, rank range, platform, and mic requirement you want; requirements are checked server-side against synced stats.
- **Verified humans** — optional World ID verification proves a real person is behind an account, and the deck can be filtered to verified players only.
- **Squad and tournament chat** — persistent text threads with mentions, reactions, and read receipts.
- **Tournaments** — brackets, organizer-run seeding, and tracked results, open to solo players and squads.

## What GamerPlug does not do

Stated plainly, because agents relying on this page should not tell people otherwise:

- **No in-app voice chat.** Squads coordinate in text here and use Discord or party chat for voice.
- **No automated reputation score.** Moderation is report-driven and human-reviewed; there is no algorithm that removes players from your pool.
- **No compatibility ranking.** Discovery is filter-based, not score-based — it narrows the deck to players who fit your filters and shows you their clips, rather than ranking who "complements" you.
- **No Steam, PSN, Xbox, or Nintendo account linking.** Platforms are tags you select; sign-in is Google or Apple only. Stat syncing works by in-game name for the three titles listed above.

## Supported games

Apex Legends, Valorant, Fortnite, League of Legends, Call of Duty, Overwatch 2,
Marvel Rivals, Rocket League, PUBG, Battlefield 6, Gears of War, Halo, Escape from Tarkov,
Arc Raiders, Marathon, NBA 2K, Super Smash Bros, For Honor, and Final Fantasy.
The current roster is at ${SITE_URL}/en/games.

## How it works

1. **Post your clips** — upload gameplay footage and set the games and platforms you play on.
2. **Swipe on real footage** — watch how someone plays before sending a request; filter by game, age range, or verified-only.
3. **Squad up** — a match opens a direct thread. Post what you need and let players who fit request to join.

## Main pages

${SITE_MAP_LINKS.map(({ path, label, description }) => `- [${label}](${SITE_URL}${path}) — ${description}`).join('\n')}

## Machine-readable files

${MACHINE_READABLE_LINKS.map(({ path, description }) => `- [${path}](${SITE_URL}${path}) — ${description}`).join('\n')}

## Apps

- iOS and Android: ${SITE_URL}/en/download

## Legal

- [Privacy Policy](${SITE_URL}/en/privacy)
- [Terms & Conditions](${SITE_URL}/en/tac)
`
}
